import React, { useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Play,
  TestTube2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Target,
  FileText,
  Building2,
  GitBranch,
  RefreshCw,
  Eye,
  Code,
  Shield,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const DISPUTE_FAMILIES = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'personal_info', label: 'Personal Information' },
  { value: 'negative_account', label: 'Negative Account' },
  { value: 'late_payment', label: 'Late Payment' },
  { value: 'collection', label: 'Collection' },
  { value: 'structural', label: 'Structural' },
  { value: 'metro2_violation', label: 'Metro 2 Violation' },
];

const METHODS = [
  { value: 'factual_inaccuracy', label: 'Factual Inaccuracy' },
  { value: 'data_breach_method', label: 'Data Breach Method' },
  { value: 'metro2_technical', label: 'Metro 2 Technical' },
  { value: 'debt_validation', label: 'Debt Validation' },
  { value: 'structural_method', label: 'Structural Method' },
  { value: 'unauthorized_inquiry', label: 'Unauthorized Inquiry' },
  { value: 'goodwill_adjustment', label: 'Goodwill Adjustment' },
];

const ITEM_TYPES = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'personal_info', label: 'Personal Info' },
  { value: 'closed_chargeoff', label: 'Closed Charge-Off' },
  { value: 'collection', label: 'Collection' },
  { value: 'closed_late_payment', label: 'Closed Late Payment' },
  { value: 'open_standard', label: 'Open Standard Account' },
  { value: 'open_revolving', label: 'Open Revolving' },
  { value: 'any_account', label: 'Any Account' },
];

const BUREAUS = [
  { value: 'equifax', label: 'Equifax' },
  { value: 'experian', label: 'Experian' },
  { value: 'transunion', label: 'TransUnion' },
];


const RuleTestingPageV2 = () => {
  const { token } = useAuth();
  const [testParams, setTestParams] = useState({
    dispute_family: 'inquiry',
    method_key: 'data_breach_method',
    round: 1,
    item_type: 'inquiry',
    reported_bureaus: ['equifax', 'experian', 'transunion'],
  });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const runTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/admin-rules/test-render`, testParams, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(response.data.test_result);
      toast.success('Test completed');
    } catch (error) {
      console.error('Test error:', error);
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  }, [testParams, token]);

  const toggleBureau = (bureau) => {
    setTestParams(prev => ({
      ...prev,
      reported_bureaus: prev.reported_bureaus.includes(bureau)
        ? prev.reported_bureaus.filter(b => b !== bureau)
        : [...prev.reported_bureaus, bureau]
    }));
  };

  return (
    <div className="min-h-screen bg-[#0B1F18] p-6" data-testid="rule-testing-page-v2">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600/20 to-pink-600/20 flex items-center justify-center">
            <TestTube2 className="text-purple-400" size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Rule Testing</h1>
            <p className="text-sm text-slate-400">Test routing verification, qualification, and letter generation</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Test Parameters Panel */}
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Play size={18} className="text-purple-400" />
            Test Parameters
          </h2>

          <div className="space-y-5">
            {/* Dispute Family */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Dispute Family</label>
              <Select 
                value={testParams.dispute_family} 
                onValueChange={(v) => setTestParams({ ...testParams, dispute_family: v })}
              >
                <SelectTrigger className="bg-stone-800 border-stone-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DISPUTE_FAMILIES.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Method */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Method</label>
              <Select 
                value={testParams.method_key} 
                onValueChange={(v) => setTestParams({ ...testParams, method_key: v })}
              >
                <SelectTrigger className="bg-stone-800 border-stone-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Round */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Round</label>
              <Select 
                value={String(testParams.round)} 
                onValueChange={(v) => setTestParams({ ...testParams, round: parseInt(v) })}
              >
                <SelectTrigger className="bg-stone-800 border-stone-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((r) => (
                    <SelectItem key={r} value={String(r)}>Round {r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Item Type */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Item Type</label>
              <Select 
                value={testParams.item_type} 
                onValueChange={(v) => setTestParams({ ...testParams, item_type: v })}
              >
                <SelectTrigger className="bg-stone-800 border-stone-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEM_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reported Bureaus */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Reported Bureaus</label>
              <div className="flex gap-3">
                {BUREAUS.map((bureau) => (
                  <button
                    key={bureau.value}
                    onClick={() => toggleBureau(bureau.value)}
                    className={`flex-1 px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                      testParams.reported_bureaus.includes(bureau.value)
                        ? 'bg-teal-500/20 text-teal-400 border-teal-500/30'
                        : 'bg-stone-800 text-slate-400 border-stone-700 hover:border-slate-600'
                    }`}
                  >
                    {bureau.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Run Test Button */}
            <Button 
              onClick={runTest} 
              disabled={testing}
              className="w-full bg-purple-600 hover:bg-purple-700 mt-6"
            >
              {testing ? (
                <><RefreshCw size={16} className="mr-2 animate-spin" /> Running Test...</>
              ) : (
                <><Play size={16} className="mr-2" /> Run Test</>
              )}
            </Button>
          </div>
        </div>

        {/* Test Results Panel */}
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Eye size={18} className="text-purple-400" />
            Test Results
          </h2>

          {testing && (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full bg-stone-800" />
              ))}
            </div>
          )}

          {!testing && !testResult && (
            <div className="text-center py-16">
              <TestTube2 size={48} className="mx-auto text-slate-600 mb-4" />
              <p className="text-slate-500">Configure parameters and run a test</p>
            </div>
          )}

          {testResult && (
            <div className="space-y-4">
              {/* Qualification Result */}
              <div className={`rounded-lg p-4 border ${
                testResult.qualification_result === 'blocked' 
                  ? 'bg-red-500/10 border-red-500/30' 
                  : testResult.qualification_result === 'default'
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-amber-500/10 border-amber-500/30'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.qualification_result === 'blocked' ? (
                    <XCircle size={18} className="text-red-400" />
                  ) : testResult.qualification_result === 'default' ? (
                    <CheckCircle size={18} className="text-emerald-400" />
                  ) : (
                    <AlertTriangle size={18} className="text-amber-400" />
                  )}
                  <span className={`font-semibold ${
                    testResult.qualification_result === 'blocked' 
                      ? 'text-red-400' 
                      : testResult.qualification_result === 'default'
                        ? 'text-emerald-400'
                        : 'text-amber-400'
                  }`}>
                    Qualification: {testResult.qualification_result.toUpperCase()}
                  </span>
                </div>
                {testResult.qualification_rules_matched?.length > 0 && (
                  <p className="text-sm text-slate-400">
                    {testResult.qualification_rules_matched[0].reason}
                  </p>
                )}
              </div>

              {/* Results Grid */}
              <div className="grid gap-3">
                <ResultRow 
                  icon={<GitBranch size={16} className="text-amber-400" />}
                  label="Method Selected"
                  value={testResult.method_selected}
                  highlight
                />
                <ResultRow 
                  icon={<FileText size={16} className="text-teal-400" />}
                  label="Template Selected"
                  value={testResult.template_selected}
                />
                <ResultRow 
                  icon={<Code size={16} className="text-slate-400" />}
                  label="Output Style"
                  value={testResult.output_style === 'flat_legal' ? 'Flat / Plain Legal' : testResult.output_style}
                  badge={testResult.output_style === 'flat_legal' ? 'enforced' : null}
                />
                <ResultRow 
                  icon={<Building2 size={16} className="text-blue-400" />}
                  label="Target Bureaus"
                  value={testResult.target_bureaus?.join(', ') || 'None'}
                />
                <ResultRow 
                  icon={<Target size={16} className="text-emerald-400" />}
                  label="Letter Count"
                  value={testResult.letter_count}
                  highlight
                />
              </div>

              {/* Global Policy Status */}
              <div className="mt-4 pt-4 border-t border-stone-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield size={16} className="text-emerald-400" />
                    <span className="text-sm text-slate-400">Flat Output Policy</span>
                  </div>
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    testResult.flat_output_enforced 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                      : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                  }`}>
                    {testResult.flat_output_enforced ? 'ENFORCED' : 'Not Enforced'}
                  </span>
                </div>
              </div>

              {/* Routing Entry Details */}
              {testResult.routing_entry && (
                <div className="mt-4 pt-4 border-t border-stone-800">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Routing Entry Details</h4>
                  <div className="bg-stone-800/50 rounded-lg p-3 text-sm font-mono">
                    <pre className="text-slate-400 overflow-x-auto">
                      {JSON.stringify(testResult.routing_entry, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ResultRow = ({ icon, label, value, highlight, badge }) => (
  <div className="flex items-center justify-between bg-stone-800/50 rounded-lg p-3">
    <div className="flex items-center gap-3">
      {icon}
      <span className="text-sm text-slate-400">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      <span className={`text-sm font-medium ${highlight ? 'text-amber-400' : 'text-white'}`}>
        {value}
      </span>
      {badge && (
        <span className="px-2 py-0.5 text-xs rounded bg-emerald-500/20 text-emerald-400">
          {badge}
        </span>
      )}
    </div>
  </div>
);

export default RuleTestingPageV2;
